import React from "react";
import { Form, Input, InputNumber, Space, Switch, Tooltip } from "antd";
import type { Rule } from "antd/es/form";
import { InfoCircleOutlined } from "@ant-design/icons";
import type { DefinitionRecord } from "../rules";

/** The field names every definition form has, for mapping server-side validation errors back. */
export const DEFINITION_FORM_FIELDS = ["name", "description", "priority", "active"];

/** The live definitions that would share `priority` with the one being edited. */
export const priorityClashWith = <T extends Pick<DefinitionRecord, "_id" | "priority" | "deleted" | "name">>(
  siblings: T[],
  priority: number | undefined | null,
  options: { excludeId?: string; inScope?: (sibling: T) => boolean } = {}
): T[] => {
  if (priority === undefined || priority === null) return [];
  return siblings.filter(
    (s) =>
      !s.deleted &&
      s.priority === priority &&
      s._id !== options.excludeId &&
      (options.inScope ? options.inScope(s) : true)
  );
};

export const fieldLabel = (label: React.ReactNode, help?: React.ReactNode) => (
  <Space>
    {label}
    {help && (
      <Tooltip title={help}>
        <InfoCircleOutlined style={{ color: "var(--primary-color)" }} />
      </Tooltip>
    )}
  </Space>
);

export interface DefinitionFormFieldsProps<T extends DefinitionRecord = DefinitionRecord> {
  /** The other live definitions, for the shared-priority warning. */
  siblings?: T[];
  /** The definition being edited (excluded from the clash check). */
  currentId?: string;
  /** Narrows which siblings compete on priority (e.g. the same stage). Defaults to all. */
  priorityScope?: (sibling: T, formValues: Record<string, any>) => boolean;
  nameHelp?: React.ReactNode;
  /** Extra antd rules on the name (e.g. names reserved by code). */
  nameRules?: Rule[];
  nameDisabled?: boolean;
  priorityHelp?: React.ReactNode;
  activeExtra?: React.ReactNode;
  /** Rendered before the name (e.g. a stage select). */
  before?: React.ReactNode;
  /** Rendered between priority and active (the feature's own fields). */
  children?: React.ReactNode;
}

/**
 * The fields every definition has — name, description, priority (with a
 * warning when another live definition shares it) and active — in the order
 * the definition forms show them. A feature's own fields slot in via
 * `before` and `children`. Renders inside an antd `<Form>`.
 */
function DefinitionFormFields<T extends DefinitionRecord = DefinitionRecord>({
  siblings = [],
  currentId,
  priorityScope,
  nameHelp,
  nameRules = [],
  nameDisabled = false,
  priorityHelp = "Definitions are evaluated lowest priority first. The first definition whose active rules all match wins.",
  activeExtra = "Inactive definitions are skipped during evaluation.",
  before,
  children,
}: DefinitionFormFieldsProps<T>) {
  return (
    <>
      {before}

      <Form.Item
        name="name"
        label={fieldLabel("Name", nameHelp)}
        rules={[{ required: true, whitespace: true, message: "Name is required" }, ...nameRules]}
      >
        <Input disabled={nameDisabled} />
      </Form.Item>

      <Form.Item name="description" label="Description">
        <Input.TextArea rows={2} />
      </Form.Item>

      <Form.Item noStyle shouldUpdate>
        {({ getFieldValue, getFieldsValue }) => {
          const values = getFieldsValue();
          const clashes = priorityClashWith(siblings, getFieldValue("priority"), {
            excludeId: currentId,
            inScope: priorityScope ? (s) => priorityScope(s, values) : undefined,
          });
          return (
            <Form.Item
              name="priority"
              label={fieldLabel("Priority", priorityHelp)}
              rules={[
                { required: true, message: "Priority is required" },
                { type: "number", min: 0, message: "Priority must be 0 or greater" },
              ]}
              validateStatus={clashes.length ? "warning" : undefined}
              help={
                clashes.length
                  ? `Shared with ${clashes.map((c) => c.name).join(", ")}. Evaluation order between them then falls back to name order.`
                  : undefined
              }
            >
              <InputNumber min={0} precision={0} style={{ width: "100%" }} />
            </Form.Item>
          );
        }}
      </Form.Item>

      {children}

      <Form.Item name="active" label="Active?" valuePropName="checked" extra={activeExtra} initialValue>
        <Switch />
      </Form.Item>
    </>
  );
}

export default DefinitionFormFields;
